import React, { useMemo, useRef, useState } from 'react';

const inputCls = 'w-full px-2.5 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100 text-sm disabled:opacity-60 disabled:bg-slate-100 dark:disabled:bg-slate-800';

/**
 * RegisterCombobox — plain free-text input (any value typed is accepted, no
 * separate "Other" mode — owner ruling, TPA-PREAUTH/tpa-bill FIX-8) with
 * type-ahead SUGGESTIONS from a fixed register (IRDAI TPAs/insurers,
 * `constants/insurerTpaRegisters.js`). Clicking or focusing the field shows
 * the FULL register as a dropdown (owner defect, live click-through
 * 2026-09-19: an empty field showed nothing, so the register looked absent);
 * typing filters it. The list is keyboard-navigable (arrows + Enter to pick,
 * Escape to dismiss) and scrolls at `max-h-64`, so 34 insurers / 17 TPAs are
 * all reachable without the popover pushing anything off-screen. No
 * combobox/autocomplete component existed in the codebase to reuse (checked
 * `src/components/common` and `package.json` — no react-select/downshift/
 * headlessui), so this is a minimal from-scratch one, scoped to this exact
 * need rather than a generic primitive nothing else needs yet.
 *
 * `value`/`onChange` carry the PLAIN STRING the caller stores
 * (insurer_name / tpa_name — no data-model change): the input's own typed
 * text IS the value at every keystroke — the register may not cover every
 * insurer/TPA, so a name that matches nothing simply saves as typed, with no
 * warning. Picking a suggestion just types its `display_name` into that same
 * string. An existing stored value that isn't in `options` (legacy free
 * text) shows as typed, unchanged.
 *
 * `maxShown` (optional; patient address State/District pickers, ~36/~75
 * entries): only the first `maxShown` matches render, with a "Showing n of N"
 * footer — typing narrows the list. Unset = every match renders (the insurer/
 * TPA registers keep their full dropdown, owner ruling above).
 *
 * `maxLength` (optional): passed to the input (address District/State = 100,
 * the server cap). Unset = no cap (insurer/TPA unchanged).
 */
export default function RegisterCombobox({ id, value, onChange, options, placeholder, disabled, testId, maxShown, maxLength }) {
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const blurTimer = useRef(null);

  const query = value || '';
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    // Empty field ⇒ the WHOLE register (the dropdown the owner asked for),
    // never an empty popover. Typing narrows it; a name that matches nothing
    // still saves as typed (free text is unchanged).
    if (!q) return options;
    return options.filter((o) => o.display_name.toLowerCase().includes(q));
  }, [options, query]);
  const shown = maxShown && filtered.length > maxShown ? filtered.slice(0, maxShown) : filtered;
  const showSuggestions = open && !disabled && shown.length > 0;

  const cancelClose = () => {
    if (blurTimer.current) clearTimeout(blurTimer.current);
  };
  const closeSoon = () => {
    // Deferred so a row's onMouseDown (which fires before this blur) can
    // still register the pick before the list unmounts.
    blurTimer.current = setTimeout(() => setOpen(false), 120);
  };

  const pick = (displayName) => {
    onChange(displayName);
    setOpen(false);
    setActiveIndex(-1);
  };

  const handleKeyDown = (e) => {
    if (!showSuggestions) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => (i + 1) % shown.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => (i - 1 + shown.length) % shown.length);
    } else if (e.key === 'Enter') {
      if (activeIndex >= 0) {
        e.preventDefault();
        pick(shown[activeIndex].display_name);
      }
    } else if (e.key === 'Escape') {
      setOpen(false);
      setActiveIndex(-1);
    }
  };

  return (
    <div className="relative">
      <input
        id={id}
        role="combobox"
        aria-expanded={showSuggestions}
        aria-controls={`${testId}-listbox`}
        aria-autocomplete="list"
        autoComplete="off"
        className={inputCls}
        value={query}
        placeholder={placeholder}
        disabled={disabled}
        maxLength={maxLength}
        data-testid={testId}
        onChange={(e) => { onChange(e.target.value); setOpen(true); setActiveIndex(-1); }}
        onFocus={() => { cancelClose(); setOpen(true); }}
        onBlur={closeSoon}
        onKeyDown={handleKeyDown}
      />
      {showSuggestions && (
        // absolute + max-h + overflow-y-auto: ~8 rows visible, rest reachable
        // by scroll — the popover overlays the flow, it never pushes the
        // Save button (or anything below) off-screen.
        <div className="absolute z-20 mt-1 w-full rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 shadow-lg text-sm overflow-hidden">
          <ul
            id={`${testId}-listbox`}
            role="listbox"
            className="max-h-64 overflow-y-auto"
            data-testid={`${testId}-listbox`}
          >
            {shown.map((o, i) => (
              <li
                key={o.name}
                role="option"
                aria-selected={i === activeIndex}
                className={`px-2.5 py-1.5 cursor-pointer ${i === activeIndex ? 'bg-teal-50 dark:bg-teal-900/30 text-teal-800 dark:text-teal-200' : 'text-slate-800 dark:text-slate-100'}`}
                onMouseDown={(e) => { e.preventDefault(); pick(o.display_name); }}
                data-testid={`${testId}-option-${o.display_name}`}
              >
                {o.display_name}
              </li>
            ))}
          </ul>
          {shown.length < filtered.length && (
            <div
              className="px-3 py-1.5 text-xs text-slate-500 dark:text-slate-400 bg-slate-50 dark:bg-slate-900 border-t border-slate-200 dark:border-slate-700"
              data-testid={`${testId}-footer`}
            >
              {`Showing ${shown.length} of ${filtered.length} - type to filter`}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
